/**
 * The boot watchdog handshake (task #125, moved out of src/main.tsx by the
 * first opening audit, finding I-3).
 *
 * hub/index.html arms a 15 second inline timer that replaces #root with a
 * plain HTML reload offer, and `window.__ldgBooted` disarms it. That flag
 * used to be set at module scope in src/main.tsx, on the line after
 * ReactDOM.createRoot(...).render(). React 18 SCHEDULES that first render;
 * it does not commit it. Proved in a browser against the production build,
 * with a setter planted on window.__ldgBooted: at the moment the flag was
 * written, #root still held the static boot shell. So the one escape that
 * does not depend on React having mounted anything was being disarmed by a
 * bundle that had merely started.
 *
 * It is set from the first COMMITTED render instead, through this hook, and
 * from exactly two places: the top level shell (src/App.tsx) and the root
 * error boundary's fallback (components/AppErrorBoundary.tsx). Those are the
 * two ways something real and actionable reaches the screen, and the honest
 * crash screen with its error report is emphatically one of them: the
 * watchdog must never wipe it out from under somebody who is reading it.
 *
 * A LAYOUT effect, not a passive one: it runs synchronously after React has
 * committed and replaced the shell markup, so there is no window in which the
 * app is on screen and the watchdog could still overwrite it.
 *
 * What is left armed, deliberately, is the case the offer exists for: a boot
 * where NOTHING commits. A dead bundle, a blocked chunk, or a render that
 * throws so hard the fallback cannot render either still reaches the offer.
 */
import { useLayoutEffect } from 'react';

export function useAppOnScreenHandshake(): void {
  useLayoutEffect(() => {
    (window as Window & { __ldgBooted?: boolean }).__ldgBooted = true;
  }, []);
}
